import React, { useEffect, useState } from "react";
import Button from "./button";
import PriceScopeChoice, { PRICE_SCOPE_OPEN } from "./price-scope-choice";

const MODULE_FIELDS = [
  { key: "eia", label: "EIA", placeholder: "250", defaultValue: 250 },
  { key: "eiaZon", label: "EIA Zon", placeholder: "250", defaultValue: 250 },
  { key: "isde", label: "ISDE", placeholder: "250", defaultValue: 250 },
  { key: "btwBatterij", label: "BTW Batterij (partner-prijs)", placeholder: "65", defaultValue: 65 },
];

/**
 * Update klant-kosten modal.
 *
 * Toont 4 inputs (één per aanvraag-module) met huidige bedragen pre-gevuld.
 * Submit stuurt `{ applicationFees: { eia, eiaZon, isde, btwBatterij } }`
 * naar de parent's onSubmit, met als tweede argument de `priceScope` als er
 * een prijs gewijzigd is (zie PriceScopeChoice).
 */
const AdminUpdateCustomerTaxModal = ({
  isOpen,
  onClose,
  onSubmit,
  applicationFees: applicationFeesProp,
  legacyApplicationFee,
}) => {
  // Lokale state als string voor input-handling (lege input = "" not 0).
  const [fees, setFees] = useState(() => makeInitialState({}));
  const [error, setError] = useState("");
  const [priceScope, setPriceScope] = useState(PRICE_SCOPE_OPEN);

  function makeInitialState(props) {
    return {
      eia: String(props?.eia ?? legacyApplicationFee ?? 250),
      eiaZon: String(props?.eiaZon ?? legacyApplicationFee ?? 250),
      isde: String(props?.isde ?? legacyApplicationFee ?? 250),
      btwBatterij: String(props?.btwBatterij ?? 65),
    };
  }

  useEffect(() => {
    setFees(makeInitialState(applicationFeesProp));
    setError("");
    setPriceScope(PRICE_SCOPE_OPEN);
    // eslint-disable-next-line
  }, [applicationFeesProp, legacyApplicationFee, isOpen]);

  useEffect(() => {
    const modal = document.getElementById("update-fees-modal");
    if (!modal) return;
    if (isOpen) {
      try { modal.showModal(); } catch { /* already open */ }
    } else if (modal.open) {
      modal.close();
    }
  }, [isOpen]);

  const handleClose = () => onClose();

  const initialFees = makeInitialState(applicationFeesProp);
  const priceChanged = MODULE_FIELDS.some(
    (f) => Number(fees[f.key]) !== Number(initialFees[f.key]),
  );

  const submitForm = () => {
    const parsed = {};
    for (const f of MODULE_FIELDS) {
      const val = fees[f.key];
      if (val === "" || val === null || val === undefined) {
        setError(`Vul ${f.label} in (€ excl. btw)`);
        return;
      }
      const num = Number(val);
      if (!Number.isFinite(num) || num < 0) {
        setError(`${f.label}: vul een geldig bedrag in`);
        return;
      }
      parsed[f.key] = num;
    }
    setError("");
    onSubmit(parsed, priceChanged ? priceScope : undefined);
    handleClose();
  };

  return (
    <dialog
      id="update-fees-modal"
      className="modal"
      onCancel={(e) => { e.preventDefault(); handleClose(); }}
    >
      <div className="modal-box max-w-lg">
        <div className="modal-content">
          <button
            className="btn btn-lg btn-circle btn-ghost absolute right-2 top-2"
            onClick={handleClose}
          >
            &times;
          </button>
          <h1 className="modal-title text-center">
            <strong>Update Klant Kosten</strong>
          </h1>
          <p className="text-center text-sm text-gray-500 mt-1">
            Bedragen zijn excl. btw, per aanvraag-module.
          </p>

          <form className="update-profile-form mt-5">
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 mt-3">
              {MODULE_FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                    {f.label}
                  </span>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-sm text-gray-400">&euro;</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={f.placeholder}
                      className="appearance-none w-full py-2 px-3 rounded-md border border-gray-300 bg-white text-gray-900 focus:border-mint-500 focus:outline-none focus:ring-1 focus:ring-mint-500"
                      value={fees[f.key]}
                      onChange={(e) =>
                        setFees((prev) => ({ ...prev, [f.key]: e.target.value }))
                      }
                    />
                  </div>
                </label>
              ))}
            </div>

            {priceChanged ? (
              <div className="mt-4">
                <PriceScopeChoice value={priceScope} onChange={setPriceScope} />
              </div>
            ) : null}

            <div className="min-h-[20px] mt-3">
              {error ? (
                <p className="text-sm text-rose-700">{error}</p>
              ) : null}
            </div>

            <div className="py-2 flex md:justify-end justify-center">
              <Button
                btnClassNames="btn-neutral w-min px-4"
                text="Update Kosten"
                type="button"
                onClick={submitForm}
              />
            </div>
          </form>
        </div>
      </div>
    </dialog>
  );
};

export default AdminUpdateCustomerTaxModal;
